/**
 * useThreadParticipants — derives an email thread's participant list.
 *
 * Extracted from the former EmailThreadParticipants summary section so the
 * same derivation feeds the header's inline avatar stack (and its popover)
 * — see `EmailThreadParticipantsStack`. Three groups, in order:
 *
 *  1. Resolved associated users (org members), alphabetical
 *  2. Resolved associated contacts, alphabetical
 *  3. Raw participant emails from the thread's email events that don't match
 *     any resolved user/contact
 *
 * The raw-email data source is the thread's timeline events (from/to/cc on
 * `ahoy_email_sent` / `ahoy_email_received` payloads). This is the only place
 * the full participant list lives — threads don't stamp participant emails
 * onto the entity itself. The viewer's own pill (resolved or raw) is labeled
 * "You", matching the conversation's sender labels.
 */

import { useMemo } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrgMembers } from '@/hooks/useOrgMembers';
import type { Participant } from '@/lib/crm/components/ParticipantPillList';
import { useAssociations } from '@/lib/crm/hooks/useAssociations';
import { useStoreResolvedParticipants } from '@/lib/crm/hooks/useStoreResolvedParticipants';
import { getEntityEmails } from '@/lib/crm/store/crmObjectStore';
import {
  extractDisplayName,
  extractEmail,
} from '@/lib/crm/utils/email-address';
import type { CrmObject } from '@/types/crm';

import { getDisplayName } from '../../utils/getDisplayName';
import { humanizeEmailLocalPart } from '../timeline/threadMessages';
import type { EmailPayload } from '../timeline/types';
import { EVENT_TYPES } from '../timeline/types';
import { useThreadTimeline } from '../timeline/useThreadTimeline';

const THREAD_TO_USER = 'ahoy_email_thread_to_user';
const THREAD_TO_CONTACT = 'ahoy_email_thread_to_contact';

export function useThreadParticipants(object: CrmObject): Participant[] {
  const { members } = useOrgMembers(object.org_id);
  const { user } = useAuthInfo();
  const viewerEmail = user?.email?.toLowerCase();

  // Pre-filter associations we care about to avoid resolving unrelated ones.
  const participantAssociations = useMemo(
    () =>
      (object.associations ?? []).filter(
        a =>
          a.association_type_name === THREAD_TO_USER ||
          a.association_type_name === THREAD_TO_CONTACT
      ),
    [object.associations]
  );

  const { associations: resolved } = useAssociations(
    object.org_id,
    participantAssociations,
    { sourceEntityTypeName: 'ahoy_email_thread' }
  );

  // The thread's email events, for mining raw participant addresses — the
  // shared thread-timeline hook owns the query and the page-until-exhausted
  // behavior (participant extraction depends on every event on the thread).
  // Other surfaces in the slideout share the same query, so this costs
  // nothing extra.
  const { events } = useThreadTimeline({
    entityId: object.entity_id,
    orgId: object.org_id,
  });

  const participants = useMemo<Participant[]>(() => {
    const users: Participant[] = [];
    const contacts: Participant[] = [];

    // Build resolved-object lists + a set of every email address that already
    // corresponds to a resolved user/contact (so we can dedupe raw pills).
    const resolvedEmails = new Set<string>();

    for (const assoc of resolved) {
      if (!assoc.object) {
        continue;
      }
      const entityEmails = getEntityEmails(assoc.object);
      // The viewer's own pill reads "You" — the same identity the
      // conversation uses for their messages, instead of a raw address or
      // a display name that may just echo the email.
      const isViewer =
        !!viewerEmail && entityEmails.some(email => email === viewerEmail);
      const item: Participant = {
        key: `${assoc.association_type_name}:${assoc.object.entity_id}`,
        object: assoc.object,
        name: isViewer ? 'You' : undefined,
      };
      if (assoc.association_type_name === THREAD_TO_USER) {
        users.push(item);
      } else if (assoc.association_type_name === THREAD_TO_CONTACT) {
        contacts.push(item);
      }
      for (const email of entityEmails) {
        resolvedEmails.add(email);
      }
    }

    // Fallback for users: a user entity may not carry `ahoy_email`, but the
    // viewer's org membership carries the auth email. Match on entity_id.
    for (const assoc of resolved) {
      if (assoc.association_type_name === THREAD_TO_USER && assoc.object) {
        const entityId = assoc.object.entity_id;
        const member = members.find(m => m.entityId === entityId);
        if (member?.email) {
          resolvedEmails.add(member.email.toLowerCase());
          if (viewerEmail && member.email.toLowerCase() === viewerEmail) {
            const item = users.find(
              u => u.object?.entity_id === entityId && !u.name
            );
            if (item) {
              item.name = 'You';
            }
          }
        }
      }
    }

    // Alphabetical within each resolved group for stable ordering.
    const byName = (a: Participant, b: Participant) => {
      const aName = a.object ? getDisplayName(a.object) : '';
      const bName = b.object ? getDisplayName(b.object) : '';
      return aName.localeCompare(bName);
    };
    users.sort(byName);
    contacts.sort(byName);

    // Walk every email event on the thread, collect unique from/to/cc addresses
    // that don't already resolve to a user/contact.
    const flatEvents = events;
    // email -> display name ('' when the header carried no name). A Map rather
    // than a Set because the name lives in the same raw header string as the
    // address; discarding it here is what made these pills render as bare
    // local-parts (j.smith, no-reply) once ingestion stopped creating a hidden
    // contact for every participant to resolve against.
    const plainEmails = new Map<string, string>();
    for (const event of flatEvents) {
      if (
        event.event_type_name !== EVENT_TYPES.EMAIL_SENT &&
        event.event_type_name !== EVENT_TYPES.EMAIL_RECEIVED
      ) {
        continue;
      }
      const payload = event.payload as EmailPayload | undefined;
      const raws: string[] = [];
      if (typeof payload?.from === 'string') {
        raws.push(payload.from);
      }
      if (Array.isArray(payload?.to)) {
        raws.push(
          ...payload.to.filter((x): x is string => typeof x === 'string')
        );
      }
      if (Array.isArray(payload?.cc)) {
        raws.push(
          ...payload.cc.filter((x): x is string => typeof x === 'string')
        );
      }
      for (const raw of raws) {
        const email = extractEmail(raw);
        if (!email || resolvedEmails.has(email)) {
          continue;
        }
        // First non-empty name wins. `from` is pushed before to/cc above, so a
        // sender's own header — the most authoritative naming of an address —
        // takes precedence over however a later recipient list spelled them.
        if (!plainEmails.get(email)) {
          const mapped = payload?.display_names?.[email];
          plainEmails.set(email, mapped || extractDisplayName(raw));
        }
      }
    }

    const plainEmailParticipants: Participant[] = Array.from(plainEmails.keys())
      .sort()
      .map(email => {
        // The viewer can surface as a plain pill too — e.g. when the thread
        // has no thread→user association (backfilled threads). Label them
        // "You" here for the same reason as the resolved branch above.
        if (viewerEmail && email === viewerEmail) {
          return { key: `email:${email}`, email, name: 'You' };
        }
        return {
          key: `email:${email}`,
          email,
          // Header display name → humanized name-shaped local part
          // (`jennifer.walsh@…` → "Jennifer Walsh") → undefined, never '':
          // ParticipantPillList falls back to the raw local-part on a falsy
          // name, and an empty string would defeat that fallback. The
          // humanized form matches how the conversation section labels the
          // same people — and gives the pill real initials ("JW", not "JE").
          name:
            plainEmails.get(email) ||
            humanizeEmailLocalPart(email) ||
            undefined,
        };
      });

    return [...users, ...contacts, ...plainEmailParticipants];
  }, [resolved, members, events, viewerEmail]);

  // Resolve plain-email participants via the CRM object store. Any email
  // that already maps to a contact / user anywhere in the org gets its
  // chip upgraded from a plain email pill to a full entity badge —
  // display name, solid border, click opens the entity's slideout
  // instead of the Create Contact modal. See
  // `useStoreResolvedParticipants` for the resolution mechanics.
  return useStoreResolvedParticipants({
    participants,
    orgId: object.org_id,
  });
}
